import { type Application, applications, type Requester } from '@labeling/globals';
import { create } from 'zustand';
import {
  canManageOperatorAccess as canManageOperatorAccessForUser,
  isAdminOrMaintainer as isAdminOrMaintainerUser,
} from '../utils/permissions';

export type UserToken = {
  roles: string[];
  oid: string;
  [key: string]: unknown;
};

type UserStore = {
  /**
   * The effective requester — either the assumed role (if one is active) or
   * the natural role derived from the token. All permission checks should read
   * from this field.
   */
  user: Requester;
  /**
   * Every role the logged-in user is entitled to based on their token. Populated
   * by setUserType. When length > 1, the UI shows the role-assumption dropdown.
   */
  availableRoles: Requester[];
  /**
   * The role the user has chosen to assume, or null when operating as their
   * natural (highest-privilege) role.
   */
  assumedRole: Requester | null;
  setUserType: (token: UserToken) => void;
  /**
   * Switch to a different role. Pass null to revert to the natural role.
   * Only roles present in availableRoles should be passed. Passing the natural
   * role (availableRoles[0]) clears the override, same as passing null.
   */
  setAssumedRole: (role: Requester | null) => void;
  isAdminOrMaintainer: () => boolean;
  canManageOperatorAccess: () => boolean;
};

const parseSandboxApplications = (value: string | undefined): Application[] => {
  if (!value) return ['combi'];
  return value
    .split(',')
    .map((item) => item.trim())
    .filter((item): item is Application => applications.includes(item as Application));
};

const readSandboxSessionValue = (key: string): string | undefined => {
  try {
    return sessionStorage.getItem(key) ?? undefined;
  } catch {
    return undefined;
  }
};

const buildSandboxRequester = (
  type: string | undefined,
  oid: string,
  appsValue: string | undefined
): Requester => {
  if (type === 'user') {
    return {
      type: 'user',
      oid,
      applications: parseSandboxApplications(appsValue),
      operatorApplications: [],
    };
  }
  if (type === 'operator') {
    return {
      type: 'operator',
      oid,
      operatorApplications: parseSandboxApplications(appsValue),
    };
  }
  if (type === 'maintainer') {
    return { type: 'maintainer', oid };
  }
  return { type: 'admin', oid };
};

/**
 * Build the initial sandbox state: the natural user and all available roles.
 *
 * VITE_SANDBOX_USER_TYPE / e2e-sandbox-requester-type sets the primary role.
 * VITE_SANDBOX_AVAILABLE_ROLES (comma-separated, e.g. "admin,user,operator")
 * declares additional roles to populate the assumption dropdown. When omitted,
 * all four roles are synthesised so the dropdown is always testable in sandbox.
 */
const getSandboxState = (): { user: Requester; availableRoles: Requester[] } => {
  const type =
    readSandboxSessionValue('e2e-sandbox-requester-type') ?? import.meta.env.VITE_SANDBOX_USER_TYPE;
  const oid =
    readSandboxSessionValue('e2e-sandbox-requester-oid') ??
    import.meta.env.VITE_SANDBOX_USER_OID ??
    'sandbox-user';
  const appsValue =
    readSandboxSessionValue('e2e-sandbox-requester-applications') ??
    import.meta.env.VITE_SANDBOX_USER_APPLICATIONS;

  const naturalUser = buildSandboxRequester(type, oid, appsValue);

  // Determine which role types to expose in the dropdown.
  const availableTypesRaw: string =
    import.meta.env.VITE_SANDBOX_AVAILABLE_ROLES ?? 'admin,maintainer,user,operator';
  const availableTypes = availableTypesRaw
    .split(',')
    .map((s: string) => s.trim())
    .filter(Boolean);

  const appsList = parseSandboxApplications(appsValue);

  const synthesised: Requester[] = availableTypes
    .map((t: string): Requester | null => {
      if (t === 'admin') return { type: 'admin', oid };
      if (t === 'maintainer') return { type: 'maintainer', oid };
      if (t === 'user')
        return { type: 'user', oid, applications: appsList, operatorApplications: [] };
      if (t === 'operator') return { type: 'operator', oid, operatorApplications: appsList };
      return null;
    })
    .filter((r): r is Requester => r !== null);

  // The natural role must be first: setAssumedRole(null) reverts to
  // availableRoles[0], so a differently-ordered list would silently escalate
  // (e.g. VITE_SANDBOX_USER_TYPE=user reverting to admin).
  const availableRoles: Requester[] = [
    naturalUser,
    ...synthesised.filter((r) => r.type !== naturalUser.type),
  ];

  return { user: naturalUser, availableRoles };
};

/**
 * Derive all roles the user is entitled to from their token. The list is
 * ordered from highest to lowest privilege so the first entry is the natural
 * role that setUserType would have resolved to.
 */
export const deriveAvailableRoles = (token: UserToken): Requester[] => {
  const { roles, oid } = token;
  const result: Requester[] = [];

  if (roles.includes('admin')) {
    result.push({ type: 'admin', oid });
  }

  if (roles.includes('maintainer')) {
    result.push({ type: 'maintainer', oid });
  }

  const appRoles = roles.filter((role) => role.startsWith('app.'));
  const operatorRoles = appRoles.filter((role) => role.endsWith('.operator'));
  const userRoles = appRoles.filter((role) => !role.endsWith('.operator'));

  const operatorApplications = operatorRoles
    .map((role) => role.replace('app.', '').replace('.operator', ''))
    .filter((app): app is Application => applications.includes(app as Application));

  const userApplications = userRoles
    .map((role) => role.replace('app.', ''))
    .filter((app): app is Application => applications.includes(app as Application));

  if (userApplications.length > 0) {
    result.push({
      type: 'user',
      oid,
      applications: userApplications,
      operatorApplications,
    });
  }

  if (operatorApplications.length > 0) {
    result.push({
      type: 'operator',
      oid,
      operatorApplications,
    });
  }

  return result;
};

const initialUser: Requester = {
  type: 'user',
  oid: 'not-initialized',
  applications: [],
  operatorApplications: [],
};

const sandboxState = import.meta.env.MODE === 'sandbox' ? getSandboxState() : null;

export const useUserStore = create<UserStore>((set, get) => ({
  user: sandboxState?.user ?? initialUser,
  availableRoles: sandboxState?.availableRoles ?? [],
  assumedRole: null,
  setUserType: (token: UserToken) => {
    const availableRoles = deriveAvailableRoles(token);
    // The natural role is the first (highest-privilege) entry. When the token
    // carries no recognised roles, fall back to a scoped user with the real oid
    // so the store is never left with oid='not-initialized' for a logged-in user.
    const naturalUser: Requester = availableRoles[0] ?? {
      type: 'user',
      oid: token.oid,
      applications: [],
      operatorApplications: [],
    };
    set({ user: naturalUser, availableRoles, assumedRole: null });
  },
  setAssumedRole: (role: Requester | null) => {
    const { availableRoles } = get();
    const naturalUser = availableRoles[0] ?? initialUser;
    if (role === null || role.type === availableRoles[0]?.type) {
      set({ assumedRole: null, user: naturalUser });
      return;
    }
    // Resolve from availableRoles so the stored Requester always carries the
    // correct applications — ignores any mismatched caller-supplied object.
    const resolved = availableRoles.find((r) => r.type === role.type);
    if (!resolved) return;
    set({ assumedRole: resolved, user: resolved });
  },
  isAdminOrMaintainer: () => {
    const { user } = get();
    return isAdminOrMaintainerUser(user);
  },
  /**
   * Users who can manage operator-to-group access: platform admin/maintainer
   * and any application user (`app.{application}`) — the latter scoped to
   * their own applications, enforced server-side. Pure operators cannot.
   */
  canManageOperatorAccess: () => {
    const { user } = get();
    return canManageOperatorAccessForUser(user);
  },
}));
